import React, { useState, useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import MobileBottomNav from './MobileBottomNav';
import { handleViewportResize, preventMobileScrollIssues, detectMobileBrowser } from '../../utils/mobileViewport';

// CSS feature detection helper
const CSS = {
  supports: (property, value) => {
    if (typeof window !== 'undefined' && window.CSS && window.CSS.supports) {
      return window.CSS.supports(property, value);
    }
    return false;
  }
};

const drawerWidth = 280;

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [browserInfo, setBrowserInfo] = useState(null);

  const handleSidebarToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  // Initialize mobile viewport handling
  useEffect(() => {
    if (isMobile) {
      const cleanup = handleViewportResize();
      preventMobileScrollIssues();
      setBrowserInfo(detectMobileBrowser());
      
      return cleanup;
    }
  }, [isMobile]);

  return (
    <Box 
      className={`
        ${isMobile ? 'mobile-viewport mobile-full-height mobile-no-horizontal-scroll mobile-touch-friendly' : ''}
        ${isMobile && CSS.supports('height', '100dvh') ? 'android-viewport-fix' : ''}
        ${isMobile && CSS.supports('-webkit-touch-callout', 'none') ? 'ios-viewport-fix' : ''}
      `}
      sx={{ 
        minHeight: '100vh',
        display: 'flex', 
        flexDirection: 'column', 
        bgcolor: 'background.default',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* Navbar at the top */}
      <Box 
        className={isMobile ? 'mobile-navbar-fixed' : ''}
        sx={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.appBar,
          width: '100%'
        }}
      >
        <Navbar onSidebarToggle={handleSidebarToggle} />
      </Box>

      {/* Content row: Sidebar on left, main content on right, both below navbar */}
      <Box sx={{ 
        display: 'flex', 
        flex: 1, 
        width: '100%', 
        height: isMobile ? 'calc(100vh - 64px)' : 'auto',
        mt: isMobile ? '56px' : '64px',
        overflow: 'hidden' 
      }}>
        {/* Sidebar */}
        <Sidebar
          open={sidebarOpen}
          onClose={handleSidebarClose}
          variant={isMobile ? "temporary" : "persistent"}
        />

        {/* Main Content */}
        <Box
          component="main"
          className={`
            ${isMobile ? 'mobile-content-area mobile-scrollable mobile-safe-content' : ''}
            ${isMobile && !browserInfo?.isIOS ? 'android-content-fix android-keyboard-adjust' : ''}
            ${isMobile && browserInfo?.isIOS ? 'ios-content-fix' : ''}
            ${isMobile && browserInfo?.isAndroid ? 'android-dynamic-height' : ''}
          `}
          sx={{
            flexGrow: 1,
            p: isMobile ? 0.75 : 3,
            width: isMobile ? '100%' : sidebarOpen ? `calc(100% - ${drawerWidth}px)` : '100%',
            maxWidth: '100vw',
            overflowX: 'hidden',
            overflowY: isMobile ? 'auto' : 'auto',
            bgcolor: 'background.default',
            ml: !isMobile && sidebarOpen ? `${drawerWidth}px` : 0,
            transition: theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}
        >
          {children}
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <Box className="mobile-bottom-nav-fixed">
          <MobileBottomNav />
        </Box>
      )}
    </Box>
  );
};

export default Layout;
